import gsap from 'gsap/gsap-core';
import { resourceHelper } from '../../../core/pixi/cashe/ResourceHelper';
import { GameDrawerBase } from '../gameDrawer/gameDrawer.base';
import {
  doorAnimations,
  pigsAnimations,
  pigsButtonAnimations,
  whiteWolfAnimations,
  wolfAnimations,
} from '../../../enums/animationNames';
import { GameLayers } from '../../../enums/GameLayers';
import { pigColors, pigIndexes } from '../../../enums/pigColors';
import { ResourcesSharedNames } from '../../../core/pixi/resources/resources.shared';
import { backgroundScenesHelper } from '../../../helpers/backgroundScenesHelper';
import { soundService, soundTypes } from '../../../services/soundService';

export class BackgroundDrawer extends GameDrawerBase {
  constructor() {
    super();
    this.__animations = {};
    this.isAnimation = false;
    this.allowJumpDependOnPig = {
      [pigColors[pigIndexes.blue]]: false,
      [pigColors[pigIndexes.red]]: false,
      [pigColors[pigIndexes.yellow]]: false,
    };
  }

  __setAnimationPromise(texture, name, events, loop = false, clear = false, trackIndex = 0) {
    return new Promise((resolve) => {
      texture.state.clearTrack();
      texture.state.setAnimation(0, name, loop);
      texture.state.addListener({
        ...events,
        complete: function (entry) {
          resolve(entry);
        },
      });
    });
  }

  _setupPigsAnimations(texture, pigColor) {
    this.__animations = {
      ...this.__animations,
      [`${pigColor}_${pigsAnimations.run}`]: (events, loop = true) => {
        this.allowJumpDependOnPig[pigColor] = true;
        if (texture.isCatched) {
          texture.isCatched = false;
        }
        return this.__setAnimationPromise(texture, pigsAnimations.run, events, loop);
      },
      [`${pigColor}_${pigsAnimations.runJump}`]: (events, loop = true) => {
        if (texture.isCatched) {
          texture.isCatched = false;
        }
        return this.__setAnimationPromise(texture, pigsAnimations.runJump, events, loop);
      },
      [`${pigColor}_${pigsAnimations.catching}`]: (events, loop = false) => {
        this.allowJumpDependOnPig[pigColor] = false;
        texture.isCatched = true;
        return this.__setAnimationPromise(texture, pigsAnimations.catching, events, loop);
      },
      [`${pigColor}_${pigsAnimations.runStart}`]: (events, loop = false) => {
        if (texture.isCatched) {
          texture.isCatched = false;
        }
        return this.__setAnimationPromise(texture, pigsAnimations.runStart, events, loop, false, 1);
      },
      [`${pigColor}_${pigsAnimations.firstPartLoop}`]: (events, loop = true) =>
        this.__setAnimationPromise(texture, pigsAnimations.firstPartLoop, events, loop),
      [`${pigColor}_${pigsAnimations.$hide}`]: () => {
        texture.visible = false;
      },
      [`${pigColor}_${pigsAnimations.$show}`]: () => {
        texture.visible = true;
      },
    };
  }

  _setupWolfAnimations(texture) {
    this.__animations = {
      ...this.__animations,
      wolfRun: (events, loop = true) =>
        this.__setAnimationPromise(texture, wolfAnimations.run, events, loop),
      wolfCatching: (events, loop = false) =>
        this.__setAnimationPromise(texture, wolfAnimations.catching, events, loop),
      wolfFirstPart: (events, loop = true) =>
        this.__setAnimationPromise(texture, wolfAnimations.firstPartLoop, events, loop),
      wolfDoorOpen: (events, loop = false) =>
        this.__setAnimationPromise(texture, wolfAnimations.doorOpening, events, loop),
      wolfCloseMouth: (events, loop = false) =>
        this.__setAnimationPromise(texture, wolfAnimations.closeMouth, events),
    };
  }

  _setupWhiteWolfAnimations(texture) {
    this.__animations = {
      ...this.__animations,
      [whiteWolfAnimations.whiteWolf]: (events) => {
        texture.visible = true;
        this.__setAnimationPromise(texture, whiteWolfAnimations.whiteWolf, events);
      },
      [whiteWolfAnimations.whiteWolfSecond]: (events) =>
        this.__setAnimationPromise(texture, whiteWolfAnimations.whiteWolfSecond, events),
    };
  }

  _setupPigsButtonAnimations({ redPigButton, yellowPigButton, bluePigButton }) {
    this.__animations = {
      ...this.__animations,
      [`${pigColors[pigIndexes.red]}_${pigsButtonAnimations.animation}`]: (events) =>
        this.__setAnimationPromise(redPigButton, pigsButtonAnimations.animation, events),
      [`${pigColors[pigIndexes.yellow]}_${pigsButtonAnimations.animation}`]: (events) =>
        this.__setAnimationPromise(yellowPigButton, pigsButtonAnimations.animation, events),
      [`${pigColors[pigIndexes.blue]}_${pigsButtonAnimations.animation}`]: (events) =>
        this.__setAnimationPromise(bluePigButton, pigsButtonAnimations.animation, events),
    };
  }

  setBackground() {
    const container = this.createContainer('backgroundContainer');
    const wallContainer = this.createContainer('wallContainer');
    const background1 = this.createSprite(resourceHelper.get(ResourcesSharedNames.backgroundFirst));
    const background2 = this.createSprite(
      resourceHelper.get(ResourcesSharedNames.backgroundSecond)
    );
    const background3 = this.createSprite(resourceHelper.get(ResourcesSharedNames.backgroundThird));
    const background4 = this.createSprite(
      resourceHelper.get(ResourcesSharedNames.backgroundSecond)
    );
    const background5 = this.createSprite(resourceHelper.get(ResourcesSharedNames.backgroundThird));
    const wall = this.createSprite(resourceHelper.get(ResourcesSharedNames.wall));
    const door = this.createSpine(resourceHelper.get(ResourcesSharedNames.door));

    wallContainer.addChild(door, wall);

    background1.anchor.set(0, 0.5);
    background2.anchor.set(0, 0.5);
    background3.anchor.set(0, 0.5);
    background4.anchor.set(0, 0.5);
    background5.anchor.set(0, 0.5);
    wall.anchor.set(0, 0.5);

    this.setPosition(background1, { x: 0, y: 400 });
    this.setPosition(background2, {
      x: backgroundScenesHelper.secondScenePositionX,
      y: 400,
    });
    this.setPosition(background3, {
      x: backgroundScenesHelper.thirdScenePositionX,
      y: 400,
    });
    this.setPosition(background4, {
      x: backgroundScenesHelper.forthScenePositionX,
      y: 400,
    });
    this.setPosition(background5, {
      x: backgroundScenesHelper.fifthScenePositionX,
      y: 400,
    });
    this.setPosition(wall, {
      x: backgroundScenesHelper.firstBlueRoom - 20,
      y: 400,
    });
    this.setPosition(door, { x: 220, y: 575 });

    const onUpdateHandler = (self) => {
      if (!this.isAnimation) {
        self.kill();
      }
    };

    const backgroundAnimation = () => {
      gsap.to(container, {
        x: -backgroundScenesHelper.forthScenePositionX,
        ease: 'none',
        duration: 4,
        onUpdate: function () {
          onUpdateHandler(this);
        },
        onComplete: () => {
          container.position.x = -backgroundScenesHelper.secondScenePositionX;
          backgroundAnimation();
        },
      });
    };

    this.__animations = {
      ...this.__animations,
      backgroundLoop: () => {
        this.isAnimation = true;
        backgroundAnimation();
      },
      killBackgroundLoop: () => {
        this.isAnimation = false;
      },
      resetBackground: () => {
        container.position.x = 0;
      },
      doorOpen: (events) => this.__setAnimationPromise(door, doorAnimations.doorOpen, events),
      doorClose: (events) =>
        this.__setAnimationPromise(door, doorAnimations.doorClose, events, true),
      hideWall: () => {
        wall.visible = false;
        door.visible = false;
      },
      showWall: () => {
        wall.visible = true;
        door.visible = true;
      },
    };

    this.__animations.doorClose();

    container.addChild(
      background1,
      background2,
      background3,
      background4,
      background5,
      wallContainer
    );
    this.layers[GameLayers.Background].addChild(container);
  }

  setupButtons() {
    const redPigButton = this.createSpine(resourceHelper.get(ResourcesSharedNames.redPigButton));
    const bluePigButton = this.createSpine(resourceHelper.get(ResourcesSharedNames.bluePigButton));
    const yellowPigButton = this.createSpine(
      resourceHelper.get(ResourcesSharedNames.yellowPigButton)
    );

    redPigButton.scale.set(this.configs.buttonConfigs.scale);
    bluePigButton.scale.set(this.configs.buttonConfigs.scale);
    yellowPigButton.scale.set(this.configs.buttonConfigs.scale);

    this.setPosition(redPigButton, this.configs.buttonConfigs.redPigButton);
    this.setPosition(yellowPigButton, this.configs.buttonConfigs.yellowPigButton);
    this.setPosition(bluePigButton, this.configs.buttonConfigs.bluePigButton);

    this._setupPigsButtonAnimations({
      redPigButton,
      yellowPigButton,
      bluePigButton,
    });

    this.__animations[`${pigColors[pigIndexes.yellow]}_${pigsButtonAnimations.animation}`]();
    this.__animations[`${pigColors[pigIndexes.red]}_${pigsButtonAnimations.animation}`]();
    this.__animations[`${pigColors[pigIndexes.blue]}_${pigsButtonAnimations.animation}`]();
    this.layers[GameLayers.GamePad].addChild(yellowPigButton, redPigButton, bluePigButton);
  }

  setupBluePig() {
    const texture = this.createSpine(resourceHelper.get(ResourcesSharedNames.bluePig));
    this.setupButtonMode(texture);

    this.registerClick(texture, () => {
      if (this.allowJumpDependOnPig[pigColors[pigIndexes.blue]] && !texture.isCatched) {
        this.allowJumpDependOnPig[pigColors[pigIndexes.blue]] = false;
        // texture.state.clearTracks();
        soundService.play(soundTypes.touch);
        this.__animations[`${pigColors[pigIndexes.blue]}_${pigsAnimations.runJump}`]().then(() => {
          texture.state.clearTracks();
          this.allowJumpDependOnPig[pigColors[pigIndexes.blue]] = true;
          if (!texture.isCatched) {
            this.__animations[`${pigColors[pigIndexes.blue]}_${pigsAnimations.run}`]();
          }
        });
      }
    });

    texture.state.timeScale = 1;
    texture.scale.set(this.configs.charactersConfigs.scale);
    this.setPosition(texture, this.configs.charactersConfigs.bluePig);
    this._setupPigsAnimations(texture, pigColors[pigIndexes.blue]);
    this.__animations[`${pigColors[pigIndexes.blue]}_${pigsAnimations.firstPartLoop}`]();
    this.layers[GameLayers.Characters].addChild(texture);
  }

  setupRedPig() {
    const texture = this.createSpine(resourceHelper.get(ResourcesSharedNames.redPig));
    this.setupButtonMode(texture);

    this.registerClick(texture, () => {
      if (this.allowJumpDependOnPig[pigColors[pigIndexes.red]] && !texture.isCatched) {
        this.allowJumpDependOnPig[pigColors[pigIndexes.red]] = false;
        // texture.state.clearTracks();
        soundService.play(soundTypes.touch);
        this.__animations[`${pigColors[pigIndexes.red]}_${pigsAnimations.runJump}`]().then(() => {
          texture.state.clearTracks();
          this.allowJumpDependOnPig[pigColors[pigIndexes.red]] = true;
          if (!texture.isCatched) {
            this.__animations[`${pigColors[pigIndexes.red]}_${pigsAnimations.run}`]();
          }
        });
      }
    });

    texture.scale.set(this.configs.charactersConfigs.scale);
    texture.state.timeScale = 1;
    this.setPosition(texture, this.configs.charactersConfigs.redPig);
    this._setupPigsAnimations(texture, pigColors[pigIndexes.red]);
    this.__animations[`${pigColors[pigIndexes.red]}_${pigsAnimations.firstPartLoop}`]();
    this.layers[GameLayers.Characters].addChild(texture);
  }

  setupYellowPig() {
    const texture = this.createSpine(resourceHelper.get(ResourcesSharedNames.yellowPig));
    this.setupButtonMode(texture);

    // texture.stateData.setMix(pigsAnimations.runJump, pigsAnimations.run, 4);
    this.registerClick(texture, () => {
      if (this.allowJumpDependOnPig[pigColors[pigIndexes.yellow]] && !texture.isCatched) {
        this.allowJumpDependOnPig[pigColors[pigIndexes.yellow]] = false;
        // texture.state.clearTracks();
        soundService.play(soundTypes.touch);
        this.__animations[`${pigColors[pigIndexes.yellow]}_${pigsAnimations.runJump}`]().then(
          () => {
            texture.state.clearTracks();
            this.allowJumpDependOnPig[pigColors[pigIndexes.yellow]] = true;
            if (!texture.isCatched) {
              this.__animations[`${pigColors[pigIndexes.yellow]}_${pigsAnimations.run}`]();
            }
          }
        );
      }
    });

    texture.state.timeScale = 1;
    texture.scale.set(this.configs.charactersConfigs.scale);
    this.setPosition(texture, this.configs.charactersConfigs.yellowPig);
    this._setupPigsAnimations(texture, pigColors[pigIndexes.yellow]);
    this.__animations[`${pigColors[pigIndexes.yellow]}_${pigsAnimations.firstPartLoop}`]();
    this.layers[GameLayers.Characters].addChild(texture);
  }

  setupWolf() {
    const texture = this.createSpine(resourceHelper.get(ResourcesSharedNames.wolf));
    texture.state.timeScale = 1;
    texture.scale.set(this.configs.charactersConfigs.scale);
    this.setPosition(texture, this.configs.charactersConfigs.wolf);
    this._setupWolfAnimations(texture);
    this.__animations.wolfFirstPart();
    this.layers[GameLayers.Characters].addChild(texture);
  }

  setupWhiteWolf() {
    const texture = this.createSpine(resourceHelper.get(ResourcesSharedNames.whiteWolf));
    texture.scale.set(this.configs.charactersConfigs.scale);
    this.setPosition(texture, this.configs.charactersConfigs.whiteWolf);
    this._setupWhiteWolfAnimations(texture);
    texture.visible = false;
    this.layers[GameLayers.Characters].addChild(texture);
  }

  clearTracks() {
    this.layers[GameLayers.Background].children = [];
    this.layers[GameLayers.Characters].children = [];
    this.layers[GameLayers.Background].removeAllListeners();
    this.layers[GameLayers.Characters].removeAllListeners();
  }

  execute(layers, configs) {
    this.layers = layers;
    this.configs = configs;
    this.setBackground();
    this.setupBluePig();
    this.setupWolf();
    this.setupRedPig();
    this.setupWhiteWolf();
    this.setupYellowPig();
  }
}

export const backgroundDrawer = new BackgroundDrawer();
